/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ArchiveNotificationPathParameters,
  DeleteNotificationPathParameters,
  GetNotificationsParams,
  GetSubscriptionsParams,
  MarkAllNotificationsReadParams,
  TLHTTPValidationError,
  TLMarkAllNotificationsReadResponse,
  TLMarkNotificationsDeliveredRequest,
  TLMarkNotificationsDeliveredResponse,
  TLMarkNotificationsReadRequest,
  TLMarkNotificationsReadResponse,
  TLMarkNotificationsUnreadRequest,
  TLMarkNotificationsUnreadResponse,
  TLNotificationArchiveResponse,
  TLNotificationCreateRequest,
  TLNotificationCreateResponse,
  TLNotificationDeleteResponse,
  TLNotificationListResponse,
  TLNotificationSubscribeResponse,
  TLNotificationSubscriptionListResponse,
  TLNotificationSubscriptionRequest,
  TLNotificationUnsubscribeResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Notifications
 */
export const getNotifications = (
  params?: GetNotificationsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationListResponse>({ url: `/notifications`, method: 'GET', params, signal }, options);
};

export const getGetNotificationsQueryKey = (params?: GetNotificationsParams) => {
  return ['notifications', ...(params ? [params] : [])] as const;
};

export const getGetNotificationsQueryOptions = <
  TData = Awaited<ReturnType<typeof getNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetNotificationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNotificationsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNotifications>>> = ({ signal }) =>
    getNotifications(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getNotifications>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof getNotifications>>>;
export type GetNotificationsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetNotifications<
  TData = Awaited<ReturnType<typeof getNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetNotificationsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNotifications>>,
          TError,
          Awaited<ReturnType<typeof getNotifications>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNotifications<
  TData = Awaited<ReturnType<typeof getNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetNotificationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNotifications>>,
          TError,
          Awaited<ReturnType<typeof getNotifications>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNotifications<
  TData = Awaited<ReturnType<typeof getNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetNotificationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Notifications
 */

export function useGetNotifications<
  TData = Awaited<ReturnType<typeof getNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetNotificationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNotificationsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Notifications
 */
export const prefetchGetNotificationsQuery = async <
  TData = Awaited<ReturnType<typeof getNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetNotificationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetNotificationsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Notifications
 */
export const invalidateGetNotifications = async (
  queryClient: QueryClient,
  params?: GetNotificationsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Notification
 */
export const createNotification = (
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationCreateResponse>(
    {
      url: `/notifications`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNotificationCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateNotificationQueryKey = (tLNotificationCreateRequest?: BodyType<TLNotificationCreateRequest>) => {
  return ['POST', 'notifications', tLNotificationCreateRequest] as const;
};

export const getCreateNotificationQueryOptions = <
  TData = Awaited<ReturnType<typeof createNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateNotificationQueryKey(tLNotificationCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createNotification>>> = ({ signal }) =>
    createNotification(tLNotificationCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createNotification>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateNotificationQueryResult = NonNullable<Awaited<ReturnType<typeof createNotification>>>;
export type CreateNotificationQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateNotification<
  TData = Awaited<ReturnType<typeof createNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNotification>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createNotification>>,
          TError,
          Awaited<ReturnType<typeof createNotification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateNotification<
  TData = Awaited<ReturnType<typeof createNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNotification>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createNotification>>,
          TError,
          Awaited<ReturnType<typeof createNotification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateNotification<
  TData = Awaited<ReturnType<typeof createNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Notification
 */

export function useCreateNotification<
  TData = Awaited<ReturnType<typeof createNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateNotificationQueryOptions(tLNotificationCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Notification
 */
export const prefetchCreateNotificationQuery = async <
  TData = Awaited<ReturnType<typeof createNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateNotificationQueryOptions(tLNotificationCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Notification
 */
export const invalidateCreateNotification = async (
  queryClient: QueryClient,
  tLNotificationCreateRequest: BodyType<TLNotificationCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateNotificationQueryKey(tLNotificationCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Mark All Notifications Read
 */
export const markAllNotificationsRead = (
  params?: MarkAllNotificationsReadParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMarkAllNotificationsReadResponse>(
    { url: `/notifications/mark-all-read`, method: 'POST', params, signal },
    options,
  );
};

export const getMarkAllNotificationsReadQueryKey = (params?: MarkAllNotificationsReadParams) => {
  return ['POST', 'notifications', 'mark-all-read', ...(params ? [params] : [])] as const;
};

export const getMarkAllNotificationsReadQueryOptions = <
  TData = Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MarkAllNotificationsReadParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMarkAllNotificationsReadQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof markAllNotificationsRead>>> = ({ signal }) =>
    markAllNotificationsRead(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof markAllNotificationsRead>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MarkAllNotificationsReadQueryResult = NonNullable<Awaited<ReturnType<typeof markAllNotificationsRead>>>;
export type MarkAllNotificationsReadQueryError = ErrorType<TLHTTPValidationError>;

export function useMarkAllNotificationsRead<
  TData = Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | MarkAllNotificationsReadParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof markAllNotificationsRead>>,
          TError,
          Awaited<ReturnType<typeof markAllNotificationsRead>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkAllNotificationsRead<
  TData = Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MarkAllNotificationsReadParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof markAllNotificationsRead>>,
          TError,
          Awaited<ReturnType<typeof markAllNotificationsRead>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkAllNotificationsRead<
  TData = Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MarkAllNotificationsReadParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Mark All Notifications Read
 */

export function useMarkAllNotificationsRead<
  TData = Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: MarkAllNotificationsReadParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMarkAllNotificationsReadQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Mark All Notifications Read
 */
export const prefetchMarkAllNotificationsReadQuery = async <
  TData = Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: MarkAllNotificationsReadParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markAllNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMarkAllNotificationsReadQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Mark All Notifications Read
 */
export const invalidateMarkAllNotificationsRead = async (
  queryClient: QueryClient,
  params?: MarkAllNotificationsReadParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getMarkAllNotificationsReadQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Mark Notifications Delivered
 */
export const markNotificationsDelivered = (
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMarkNotificationsDeliveredResponse>(
    {
      url: `/notifications/mark-delivered`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLMarkNotificationsDeliveredRequest,
      signal,
    },
    options,
  );
};

export const getMarkNotificationsDeliveredQueryKey = (
  tLMarkNotificationsDeliveredRequest?: BodyType<TLMarkNotificationsDeliveredRequest>,
) => {
  return ['POST', 'notifications', 'mark-delivered', tLMarkNotificationsDeliveredRequest] as const;
};

export const getMarkNotificationsDeliveredQueryOptions = <
  TData = Awaited<ReturnType<typeof markNotificationsDelivered>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsDelivered>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMarkNotificationsDeliveredQueryKey(tLMarkNotificationsDeliveredRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof markNotificationsDelivered>>> = ({ signal }) =>
    markNotificationsDelivered(tLMarkNotificationsDeliveredRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof markNotificationsDelivered>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MarkNotificationsDeliveredQueryResult = NonNullable<Awaited<ReturnType<typeof markNotificationsDelivered>>>;
export type MarkNotificationsDeliveredQueryError = ErrorType<TLHTTPValidationError>;

export function useMarkNotificationsDelivered<
  TData = Awaited<ReturnType<typeof markNotificationsDelivered>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsDelivered>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof markNotificationsDelivered>>,
          TError,
          Awaited<ReturnType<typeof markNotificationsDelivered>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkNotificationsDelivered<
  TData = Awaited<ReturnType<typeof markNotificationsDelivered>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsDelivered>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof markNotificationsDelivered>>,
          TError,
          Awaited<ReturnType<typeof markNotificationsDelivered>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkNotificationsDelivered<
  TData = Awaited<ReturnType<typeof markNotificationsDelivered>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsDelivered>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Mark Notifications Delivered
 */

export function useMarkNotificationsDelivered<
  TData = Awaited<ReturnType<typeof markNotificationsDelivered>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsDelivered>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMarkNotificationsDeliveredQueryOptions(tLMarkNotificationsDeliveredRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Mark Notifications Delivered
 */
export const prefetchMarkNotificationsDeliveredQuery = async <
  TData = Awaited<ReturnType<typeof markNotificationsDelivered>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsDelivered>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMarkNotificationsDeliveredQueryOptions(tLMarkNotificationsDeliveredRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Mark Notifications Delivered
 */
export const invalidateMarkNotificationsDelivered = async (
  queryClient: QueryClient,
  tLMarkNotificationsDeliveredRequest: BodyType<TLMarkNotificationsDeliveredRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMarkNotificationsDeliveredQueryKey(tLMarkNotificationsDeliveredRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Mark Notifications Read
 */
export const markNotificationsRead = (
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMarkNotificationsReadResponse>(
    {
      url: `/notifications/mark-read`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLMarkNotificationsReadRequest,
      signal,
    },
    options,
  );
};

export const getMarkNotificationsReadQueryKey = (
  tLMarkNotificationsReadRequest?: BodyType<TLMarkNotificationsReadRequest>,
) => {
  return ['POST', 'notifications', 'mark-read', tLMarkNotificationsReadRequest] as const;
};

export const getMarkNotificationsReadQueryOptions = <
  TData = Awaited<ReturnType<typeof markNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMarkNotificationsReadQueryKey(tLMarkNotificationsReadRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof markNotificationsRead>>> = ({ signal }) =>
    markNotificationsRead(tLMarkNotificationsReadRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof markNotificationsRead>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MarkNotificationsReadQueryResult = NonNullable<Awaited<ReturnType<typeof markNotificationsRead>>>;
export type MarkNotificationsReadQueryError = ErrorType<TLHTTPValidationError>;

export function useMarkNotificationsRead<
  TData = Awaited<ReturnType<typeof markNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsRead>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof markNotificationsRead>>,
          TError,
          Awaited<ReturnType<typeof markNotificationsRead>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkNotificationsRead<
  TData = Awaited<ReturnType<typeof markNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsRead>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof markNotificationsRead>>,
          TError,
          Awaited<ReturnType<typeof markNotificationsRead>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkNotificationsRead<
  TData = Awaited<ReturnType<typeof markNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Mark Notifications Read
 */

export function useMarkNotificationsRead<
  TData = Awaited<ReturnType<typeof markNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMarkNotificationsReadQueryOptions(tLMarkNotificationsReadRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Mark Notifications Read
 */
export const prefetchMarkNotificationsReadQuery = async <
  TData = Awaited<ReturnType<typeof markNotificationsRead>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsRead>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMarkNotificationsReadQueryOptions(tLMarkNotificationsReadRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Mark Notifications Read
 */
export const invalidateMarkNotificationsRead = async (
  queryClient: QueryClient,
  tLMarkNotificationsReadRequest: BodyType<TLMarkNotificationsReadRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMarkNotificationsReadQueryKey(tLMarkNotificationsReadRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Mark Notifications Unread
 */
export const markNotificationsUnread = (
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLMarkNotificationsUnreadResponse>(
    {
      url: `/notifications/mark-unread`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLMarkNotificationsUnreadRequest,
      signal,
    },
    options,
  );
};

export const getMarkNotificationsUnreadQueryKey = (
  tLMarkNotificationsUnreadRequest?: BodyType<TLMarkNotificationsUnreadRequest>,
) => {
  return ['POST', 'notifications', 'mark-unread', tLMarkNotificationsUnreadRequest] as const;
};

export const getMarkNotificationsUnreadQueryOptions = <
  TData = Awaited<ReturnType<typeof markNotificationsUnread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsUnread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMarkNotificationsUnreadQueryKey(tLMarkNotificationsUnreadRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof markNotificationsUnread>>> = ({ signal }) =>
    markNotificationsUnread(tLMarkNotificationsUnreadRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof markNotificationsUnread>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type MarkNotificationsUnreadQueryResult = NonNullable<Awaited<ReturnType<typeof markNotificationsUnread>>>;
export type MarkNotificationsUnreadQueryError = ErrorType<TLHTTPValidationError>;

export function useMarkNotificationsUnread<
  TData = Awaited<ReturnType<typeof markNotificationsUnread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsUnread>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof markNotificationsUnread>>,
          TError,
          Awaited<ReturnType<typeof markNotificationsUnread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkNotificationsUnread<
  TData = Awaited<ReturnType<typeof markNotificationsUnread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsUnread>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof markNotificationsUnread>>,
          TError,
          Awaited<ReturnType<typeof markNotificationsUnread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useMarkNotificationsUnread<
  TData = Awaited<ReturnType<typeof markNotificationsUnread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsUnread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Mark Notifications Unread
 */

export function useMarkNotificationsUnread<
  TData = Awaited<ReturnType<typeof markNotificationsUnread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsUnread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getMarkNotificationsUnreadQueryOptions(tLMarkNotificationsUnreadRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Mark Notifications Unread
 */
export const prefetchMarkNotificationsUnreadQuery = async <
  TData = Awaited<ReturnType<typeof markNotificationsUnread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof markNotificationsUnread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getMarkNotificationsUnreadQueryOptions(tLMarkNotificationsUnreadRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Mark Notifications Unread
 */
export const invalidateMarkNotificationsUnread = async (
  queryClient: QueryClient,
  tLMarkNotificationsUnreadRequest: BodyType<TLMarkNotificationsUnreadRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getMarkNotificationsUnreadQueryKey(tLMarkNotificationsUnreadRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Notification Stream
 */
export const notificationStream = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<unknown>({ url: `/notifications/stream`, method: 'GET', signal }, options);
};

export const getNotificationStreamQueryKey = () => {
  return ['notifications', 'stream'] as const;
};

export const getNotificationStreamQueryOptions = <
  TData = Awaited<ReturnType<typeof notificationStream>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationStream>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getNotificationStreamQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof notificationStream>>> = ({ signal }) =>
    notificationStream(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof notificationStream>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type NotificationStreamQueryResult = NonNullable<Awaited<ReturnType<typeof notificationStream>>>;
export type NotificationStreamQueryError = ErrorType<unknown>;

export function useNotificationStream<
  TData = Awaited<ReturnType<typeof notificationStream>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationStream>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof notificationStream>>,
          TError,
          Awaited<ReturnType<typeof notificationStream>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useNotificationStream<
  TData = Awaited<ReturnType<typeof notificationStream>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationStream>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof notificationStream>>,
          TError,
          Awaited<ReturnType<typeof notificationStream>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useNotificationStream<
  TData = Awaited<ReturnType<typeof notificationStream>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationStream>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Notification Stream
 */

export function useNotificationStream<
  TData = Awaited<ReturnType<typeof notificationStream>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationStream>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getNotificationStreamQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Notification Stream
 */
export const prefetchNotificationStreamQuery = async <
  TData = Awaited<ReturnType<typeof notificationStream>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof notificationStream>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getNotificationStreamQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Notification Stream
 */
export const invalidateNotificationStream = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getNotificationStreamQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Subscribe To Notifications
 */
export const subscribeToNotifications = (
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationSubscribeResponse>(
    {
      url: `/notifications/subscribe`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNotificationSubscriptionRequest,
      signal,
    },
    options,
  );
};

export const getSubscribeToNotificationsQueryKey = (
  tLNotificationSubscriptionRequest?: BodyType<TLNotificationSubscriptionRequest>,
) => {
  return ['POST', 'notifications', 'subscribe', tLNotificationSubscriptionRequest] as const;
};

export const getSubscribeToNotificationsQueryOptions = <
  TData = Awaited<ReturnType<typeof subscribeToNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof subscribeToNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSubscribeToNotificationsQueryKey(tLNotificationSubscriptionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof subscribeToNotifications>>> = ({ signal }) =>
    subscribeToNotifications(tLNotificationSubscriptionRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof subscribeToNotifications>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SubscribeToNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof subscribeToNotifications>>>;
export type SubscribeToNotificationsQueryError = ErrorType<TLHTTPValidationError>;

export function useSubscribeToNotifications<
  TData = Awaited<ReturnType<typeof subscribeToNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof subscribeToNotifications>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof subscribeToNotifications>>,
          TError,
          Awaited<ReturnType<typeof subscribeToNotifications>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubscribeToNotifications<
  TData = Awaited<ReturnType<typeof subscribeToNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof subscribeToNotifications>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof subscribeToNotifications>>,
          TError,
          Awaited<ReturnType<typeof subscribeToNotifications>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubscribeToNotifications<
  TData = Awaited<ReturnType<typeof subscribeToNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof subscribeToNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Subscribe To Notifications
 */

export function useSubscribeToNotifications<
  TData = Awaited<ReturnType<typeof subscribeToNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof subscribeToNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSubscribeToNotificationsQueryOptions(tLNotificationSubscriptionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Subscribe To Notifications
 */
export const prefetchSubscribeToNotificationsQuery = async <
  TData = Awaited<ReturnType<typeof subscribeToNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof subscribeToNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSubscribeToNotificationsQueryOptions(tLNotificationSubscriptionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Subscribe To Notifications
 */
export const invalidateSubscribeToNotifications = async (
  queryClient: QueryClient,
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSubscribeToNotificationsQueryKey(tLNotificationSubscriptionRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Subscriptions
 */
export const getSubscriptions = (
  params?: GetSubscriptionsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationSubscriptionListResponse>(
    { url: `/notifications/subscriptions`, method: 'GET', params, signal },
    options,
  );
};

export const getGetSubscriptionsQueryKey = (params?: GetSubscriptionsParams) => {
  return ['notifications', 'subscriptions', ...(params ? [params] : [])] as const;
};

export const getGetSubscriptionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getSubscriptions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSubscriptionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSubscriptions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSubscriptionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSubscriptions>>> = ({ signal }) =>
    getSubscriptions(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSubscriptions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSubscriptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getSubscriptions>>>;
export type GetSubscriptionsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSubscriptions<
  TData = Awaited<ReturnType<typeof getSubscriptions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetSubscriptionsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSubscriptions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSubscriptions>>,
          TError,
          Awaited<ReturnType<typeof getSubscriptions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSubscriptions<
  TData = Awaited<ReturnType<typeof getSubscriptions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSubscriptionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSubscriptions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSubscriptions>>,
          TError,
          Awaited<ReturnType<typeof getSubscriptions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSubscriptions<
  TData = Awaited<ReturnType<typeof getSubscriptions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSubscriptionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSubscriptions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Subscriptions
 */

export function useGetSubscriptions<
  TData = Awaited<ReturnType<typeof getSubscriptions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSubscriptionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSubscriptions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSubscriptionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Subscriptions
 */
export const prefetchGetSubscriptionsQuery = async <
  TData = Awaited<ReturnType<typeof getSubscriptions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetSubscriptionsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSubscriptions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSubscriptionsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Subscriptions
 */
export const invalidateGetSubscriptions = async (
  queryClient: QueryClient,
  params?: GetSubscriptionsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSubscriptionsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Unsubscribe From Notifications
 */
export const unsubscribeFromNotifications = (
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationUnsubscribeResponse>(
    {
      url: `/notifications/unsubscribe`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNotificationSubscriptionRequest,
      signal,
    },
    options,
  );
};

export const getUnsubscribeFromNotificationsQueryKey = (
  tLNotificationSubscriptionRequest?: BodyType<TLNotificationSubscriptionRequest>,
) => {
  return ['POST', 'notifications', 'unsubscribe', tLNotificationSubscriptionRequest] as const;
};

export const getUnsubscribeFromNotificationsQueryOptions = <
  TData = Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUnsubscribeFromNotificationsQueryKey(tLNotificationSubscriptionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof unsubscribeFromNotifications>>> = ({ signal }) =>
    unsubscribeFromNotifications(tLNotificationSubscriptionRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UnsubscribeFromNotificationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof unsubscribeFromNotifications>>
>;
export type UnsubscribeFromNotificationsQueryError = ErrorType<TLHTTPValidationError>;

export function useUnsubscribeFromNotifications<
  TData = Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromNotifications>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
          TError,
          Awaited<ReturnType<typeof unsubscribeFromNotifications>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUnsubscribeFromNotifications<
  TData = Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromNotifications>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
          TError,
          Awaited<ReturnType<typeof unsubscribeFromNotifications>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUnsubscribeFromNotifications<
  TData = Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Unsubscribe From Notifications
 */

export function useUnsubscribeFromNotifications<
  TData = Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUnsubscribeFromNotificationsQueryOptions(tLNotificationSubscriptionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Unsubscribe From Notifications
 */
export const prefetchUnsubscribeFromNotificationsQuery = async <
  TData = Awaited<ReturnType<typeof unsubscribeFromNotifications>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof unsubscribeFromNotifications>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUnsubscribeFromNotificationsQueryOptions(tLNotificationSubscriptionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Unsubscribe From Notifications
 */
export const invalidateUnsubscribeFromNotifications = async (
  queryClient: QueryClient,
  tLNotificationSubscriptionRequest: BodyType<TLNotificationSubscriptionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUnsubscribeFromNotificationsQueryKey(tLNotificationSubscriptionRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Notification
 */
export const deleteNotification = (
  { notificationId }: DeleteNotificationPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationDeleteResponse>(
    { url: `/notifications/${notificationId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteNotificationQueryKey = ({ notificationId }: DeleteNotificationPathParameters) => {
  return ['DELETE', 'notifications', notificationId] as const;
};

export const getDeleteNotificationQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { notificationId }: DeleteNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteNotificationQueryKey({ notificationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteNotification>>> = ({ signal }) =>
    deleteNotification({ notificationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: notificationId !== null && notificationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteNotificationQueryResult = NonNullable<Awaited<ReturnType<typeof deleteNotification>>>;
export type DeleteNotificationQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteNotification<
  TData = Awaited<ReturnType<typeof deleteNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNotificationPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteNotification>>,
          TError,
          Awaited<ReturnType<typeof deleteNotification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteNotification<
  TData = Awaited<ReturnType<typeof deleteNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteNotification>>,
          TError,
          Awaited<ReturnType<typeof deleteNotification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteNotification<
  TData = Awaited<ReturnType<typeof deleteNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Notification
 */

export function useDeleteNotification<
  TData = Awaited<ReturnType<typeof deleteNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { notificationId }: DeleteNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteNotificationQueryOptions({ notificationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Notification
 */
export const prefetchDeleteNotificationQuery = async <
  TData = Awaited<ReturnType<typeof deleteNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { notificationId }: DeleteNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteNotificationQueryOptions({ notificationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Notification
 */
export const invalidateDeleteNotification = async (
  queryClient: QueryClient,
  { notificationId }: DeleteNotificationPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteNotificationQueryKey({ notificationId }) }, options);

  return queryClient;
};

/**
 * @summary Archive Notification
 */
export const archiveNotification = (
  { notificationId }: ArchiveNotificationPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNotificationArchiveResponse>(
    { url: `/notifications/${notificationId}/archive`, method: 'POST', signal },
    options,
  );
};

export const getArchiveNotificationQueryKey = ({ notificationId }: ArchiveNotificationPathParameters) => {
  return ['POST', 'notifications', notificationId, 'archive'] as const;
};

export const getArchiveNotificationQueryOptions = <
  TData = Awaited<ReturnType<typeof archiveNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { notificationId }: ArchiveNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getArchiveNotificationQueryKey({ notificationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof archiveNotification>>> = ({ signal }) =>
    archiveNotification({ notificationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: notificationId !== null && notificationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ArchiveNotificationQueryResult = NonNullable<Awaited<ReturnType<typeof archiveNotification>>>;
export type ArchiveNotificationQueryError = ErrorType<TLHTTPValidationError>;

export function useArchiveNotification<
  TData = Awaited<ReturnType<typeof archiveNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveNotificationPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveNotification>>,
          TError,
          Awaited<ReturnType<typeof archiveNotification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveNotification<
  TData = Awaited<ReturnType<typeof archiveNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof archiveNotification>>,
          TError,
          Awaited<ReturnType<typeof archiveNotification>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useArchiveNotification<
  TData = Awaited<ReturnType<typeof archiveNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ArchiveNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Archive Notification
 */

export function useArchiveNotification<
  TData = Awaited<ReturnType<typeof archiveNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { notificationId }: ArchiveNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getArchiveNotificationQueryOptions({ notificationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Archive Notification
 */
export const prefetchArchiveNotificationQuery = async <
  TData = Awaited<ReturnType<typeof archiveNotification>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { notificationId }: ArchiveNotificationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof archiveNotification>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getArchiveNotificationQueryOptions({ notificationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Archive Notification
 */
export const invalidateArchiveNotification = async (
  queryClient: QueryClient,
  { notificationId }: ArchiveNotificationPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getArchiveNotificationQueryKey({ notificationId }) }, options);

  return queryClient;
};
